import type { MetaEvent } from './types';

/** Incremental worker output. Kept outside the deterministic command queue. */
export interface DiagnosticsChunk {
  type: 'diagnostics_chunk';
  v: 1;
  meta?: MetaEvent;
  jsonl: string;
  eventCount: number;
}

export interface ArchiveRecord {
  id: string;
  matchId: string;
  chunk: DiagnosticsChunk;
}

export interface DiagnosticsStore {
  put(record: ArchiveRecord): Promise<void>;
  putMany?(records: readonly ArchiveRecord[]): Promise<void>;
  readAll(): Promise<ArchiveRecord[]>;
}

export interface DiagnosticsArchive {
  append(matchId: string, chunk: DiagnosticsChunk): void;
  exportJsonl(): Promise<string | undefined>;
  flush(): Promise<void>;
}

const EXPORT_STORAGE_WAIT_MS = 10_000;

async function boundedStorageWait<T>(operation: Promise<T>, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), EXPORT_STORAGE_WAIT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** No expiry or size cap: failed or pending writes remain available in memory for export. */
export function createDiagnosticsArchive(
  store: DiagnosticsStore,
  onStorageError: (error: unknown) => void = () => {},
): DiagnosticsArchive {
  const session = new Map<string, ArchiveRecord>();
  let sequence = 0;
  const pending: { record: ArchiveRecord; sequence: number }[] = [];
  const waiters: { target: number; resolve: () => void }[] = [];
  let settledThrough = 0;
  let writing = false;

  const pump = async (): Promise<void> => {
    while (pending.length) {
      // One transaction includes everything accumulated during the previous commit.
      // Keep record IDs and metadata boundaries: batching must not merge matches.
      const batch = pending.splice(0);
      try {
        const records = batch.map(({ record }) => record);
        if (store.putMany) await store.putMany(records);
        else for (const record of records) await store.put(record);
      } catch (error) {
        onStorageError(error);
      }
      settledThrough = batch[batch.length - 1]!.sequence;
      for (let index = waiters.length - 1; index >= 0; index--) {
        if (waiters[index]!.target > settledThrough) continue;
        waiters.splice(index, 1)[0]!.resolve();
      }
    }
    writing = false;
  };
  const waitForWrites = () => {
    // Later ticks cannot extend a caller's durability boundary indefinitely.
    const target = sequence;
    let release = () => {};
    const promise = new Promise<void>((resolve) => {
      release = resolve;
    });
    const waiter = { target, resolve: release };
    if (settledThrough >= target) release();
    else waiters.push(waiter);
    return {
      promise,
      cancel() {
        const index = waiters.indexOf(waiter);
        if (index >= 0) waiters.splice(index, 1);
      },
    };
  };
  const flush = (): Promise<void> => waitForWrites().promise;
  return {
    append(matchId, chunk) {
      const id = `${matchId}:${String(sequence++).padStart(12, '0')}`;
      const record: ArchiveRecord = { id, matchId, chunk };
      session.set(id, record);
      pending.push({ record, sequence });
      if (!writing) {
        writing = true;
        queueMicrotask(() => void pump());
      }
    },
    flush,
    async exportJsonl() {
      const barrier = waitForWrites();
      try {
        await boundedStorageWait(
          barrier.promise,
          'Diagnostics writes are still pending; export uses received session records',
        );
      } catch (error) {
        // Export can proceed from session memory; the original write keeps running.
        // The warning makes lack of durability/history explicit to the player.
        onStorageError(error);
      } finally {
        barrier.cancel();
      }
      let saved: ArchiveRecord[] = [];
      try {
        saved = await boundedStorageWait(
          store.readAll(),
          'Diagnostics history read timed out; earlier matches may be unavailable',
        );
      } catch (error) {
        onStorageError(error);
      }
      const records = new Map(saved.map((record) => [record.id, record]));
      for (const [id, record] of session) records.set(id, record);
      const matches = new Map<string, { meta: MetaEvent; chunks: string[]; count: number }>();
      for (const record of [...records.values()].sort((a, b) => a.id.localeCompare(b.id))) {
        const { matchId, chunk } = record;
        if (chunk.meta) matches.set(matchId, { meta: chunk.meta, chunks: [], count: 0 });
        const match = matches.get(matchId);
        if (!match) continue;
        match.chunks.push(chunk.jsonl);
        match.count += chunk.eventCount;
      }
      if (matches.size === 0) return undefined;
      return [...matches.values()]
        .map(
          ({ meta, chunks, count }) =>
            JSON.stringify({ ...meta, droppedCount: 0, totalSeen: count }) + '\n' + chunks.join(''),
        )
        .join('');
    },
  };
}

/** Append-only IndexedDB chunks avoid rewriting an ever-growing match every tick. */
export function createIndexedDbDiagnosticsStore(): DiagnosticsStore {
  let connection: Promise<IDBDatabase> | undefined;
  const open = (): Promise<IDBDatabase> => {
    connection ??= new Promise((resolve, reject) => {
      const request = indexedDB.open('rts-reforged-diagnostics', 1);
      request.onupgradeneeded = () => request.result.createObjectStore('chunks', { keyPath: 'id' });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(request.error ?? new Error('Diagnostics storage request failed'));
      request.onblocked = () => reject(new Error('Diagnostics storage is blocked'));
    });
    return connection;
  };
  const putMany = async (records: readonly ArchiveRecord[]): Promise<void> => {
    const db = await open();
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('chunks', 'readwrite');
      const chunks = transaction.objectStore('chunks');
      for (const record of records) chunks.put(record);
      transaction.oncomplete = () => resolve();
      transaction.onabort = () =>
        reject(transaction.error ?? new Error('Diagnostics storage transaction failed'));
      transaction.onerror = () =>
        reject(transaction.error ?? new Error('Diagnostics storage transaction failed'));
    });
  };
  return {
    put: (record) => putMany([record]),
    putMany,
    async readAll() {
      const db = await open();
      return new Promise((resolve, reject) => {
        const request = db.transaction('chunks').objectStore('chunks').getAll();
        request.onsuccess = () => resolve(request.result as ArchiveRecord[]);
        request.onerror = () =>
          reject(request.error ?? new Error('Diagnostics storage request failed'));
      });
    },
  };
}
